import React, { useEffect, useRef, useState } from "react";
import { Download, Printer, Loader2, AlertCircle, FileText, Eye, Upload, Loader, CheckCircle, X, } from "lucide-react";
import apiService from "../config";
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';

// ─────────────────────────────────────────────
// Utility helpers (shared across all sections)
// ─────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const today = new Date();
const todayFormatted = formatDate(today.toISOString());

/** Reads the best proposalId from a project object */
const findProposalId = (projectObj) => {
  if (!projectObj) return null;
  const keys = [
    "acquisition_proposal_no", "land_acquisition_no",
    "proposal_id", "proposalId", "proposal", "id", "project_id",
  ];
  for (const key of keys) {
    const val = projectObj[key];
    if (val !== undefined && val !== null && val !== "") return val;
  }
  return null;
};

// ─────────────────────────────────────────────
// ViewProposal Content (नमुना "क")
// ─────────────────────────────────────────────
const ViewProposalContent = ({ proposal, projectName, onViewDocument, formattedDate,
  inchargeName,
  inchargeDesig,
  regionalName,
  regionalDesig, }) => {
  if (!proposal) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
        प्रस्ताव माहिती उपलब्ध नाही
      </div>
    );
  }

  const renderYesNo = (value) => (value ? "होय" : "नाही");
  const districts = proposal?.dtv?.map(item => item.dist_name).filter(Boolean);
const talukas = proposal?.dtv?.map(item => item.taluka_name).filter(Boolean);
const villages = proposal?.dtv?.map(item => item.village_name).filter(Boolean);

// Remove duplicates (optional but recommended)
const uniqueDistricts = [...new Set(districts)];
const uniqueTalukas = [...new Set(talukas)];
const uniqueVillages = [...new Set(villages)];

  // Helper to render Yes/No with optional View button
  const renderYesNoWithFile = (value, fileUrl) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span>{renderYesNo(value)}</span>
      {fileUrl && (
        <button
          className="no-print"
          onClick={() => onViewDocument && onViewDocument(fileUrl)}
          style={{
            background: "#3b82f6",
            color: "#fff",
            padding: "4px 12px",
            borderRadius: 4,
            border: "none",
            fontSize: 11,
            cursor: "pointer",
            fontWeight: 600,
          }}
          onMouseOver={(e) => (e.target.style.background = "#2563eb")}
          onMouseOut={(e) => (e.target.style.background = "#3b82f6")}
        >
          View
        </button>
      )}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }} className="font-bold">
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>नमुना "क"</h2>
        <p>
         <span className="">प्रकल्प नाव:</span> {projectName || "N/A"} साठी खाजगी जमिनीच्या भूसंपादचा प्रस्ताव 
        </p>
        <p>प्रस्ताव नाव :{proposal.acquisition_proposal_no} </p>
        
      </div>

      {/* Main proposal table */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: "2px solid black", fontSize: 13 }}>
        <tbody>
          {/* Row 1 */}
          <tr>
            <td style={vpTdStyle} width="5%">१</td>
            <td style={vpTdStyle} width="55%">	भूसंपादन प्रस्ताव पाठविणाऱ्या अधिका-याचे नाव,पदनाम व पत्ता</td>
            <td style={vpTdStyle}>
              <div><strong>नाव:</strong> {proposal.acquisition_incharge_name || "N/A"}</div>
              <div><strong>पदनाम:</strong> {proposal.acquisition_incharge_designation || "-"}</div>
              <div><strong>पत्ता:</strong> {proposal.acquisition_incharge_address || "-"}</div>
            </td>
          </tr>

          {/* Row 2 */}
          <tr>
            <td style={vpTdStyle}>२</td>
            <td style={vpTdStyle}>भूसंपादन प्रस्ताव सादर करणाऱ्या प्रादेशिक विभागाचे / प्राधिकरणाचे / महामंडळाचे नाव व पत्ता</td>
            <td style={vpTdStyle}>{proposal.acquisition_agency || "-"}</td>
          </tr>

          {/* Row 3 */}
          <tr>
            <td style={vpTdStyle}>३</td>
            <td style={vpTdStyle}>मंत्रालयीन स्तरावरील संबंधीत प्रशासकीय विभाग</td>
            <td style={vpTdStyle}>{proposal.acquisition_department || "-"}</td>
          </tr>

          {/* Row 4 */}
          <tr>
            <td style={vpTdStyle}>४</td>
            <td style={vpTdStyle}>जमीन कोणत्या कारणासाठी संपादन करून हवी आहे ?</td>
            <td style={vpTdStyle}>{proposal.acquisition_purpose || "-"}</td>
          </tr>

          {/* Row 5 */}
          <tr>
            <td style={vpTdStyle}>५</td>
            <td style={vpTdStyle}>जमीन कोणत्या शासन धोरणानुसार/कायद्यानुसार संपादन करून हवी आहे ?</td>
            <td style={vpTdStyle}>{proposal.acquisition_government_policy || "-"}</td>
          </tr>

          {/* Row 6 */}
          <tr>
            <td style={vpTdStyle}>६</td>
            <td style={vpTdStyle}>
              संपादन करावयाच्या जमिनी ज्या क्षेत्रात मोडतात ते गाव, तालुका, जिल्हा.
            </td>
            <td style={vpTdStyle}>
              <div><strong>जिल्हा:</strong> {uniqueDistricts.join(", ") || "-"}</div>
              <div><strong>तालुका:</strong> {uniqueTalukas.join(", ") || "-"}</div>
              <div><strong>गाव:</strong> {uniqueVillages.join(", ") || "-"}</div>
            </td>
          </tr>

          {/* Row 7 */}
          <tr>
            <td style={vpTdStyle}>७</td>
            <td style={vpTdStyle}>संपादन करून हवे असलेले एकूण क्षेत्र</td>
            <td style={vpTdStyle}>
              <div><strong>एकूण क्षेत्र:</strong> {proposal.total_area || "-"} </div>
              <div><strong>संपादन क्षेत्र:</strong> {proposal.aquired_area || "-"} </div>
            </td>
          </tr>

          {/* Row 8 */}
          <tr>
            <td style={vpTdStyle}>८</td>
            <td style={vpTdStyle}>गावनिहाय व गट/सर्व्हे क्र. नुसार आणि संपादन क्षेत्राच्या चतुःसीमा दाखविणारी अनुसूची यासोबत जोडणे आवश्यक आहे, त्याप्रमाणे ती जोडली आहे काय ?(परिशिष्ट 6)</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.schedule_6_08, proposal.schedule_6_08_file)}</td>
          </tr>

          {/* Row 9 */}
          <tr>
            <td style={vpTdStyle}>९</td>
            <td style={vpTdStyle}>संपादन करावयाच्या क्षेत्राचा चिन्हांकित / साक्षांकित नकाशा प्रस्तावासोबत जोडणे आवश्यक आहे, त्या प्रमाणे जोडला आहे काय ? भूमिअभिलेख विभागाकडील गावाचे नकाशे दोन प्रतीत सादर केले आहे काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.schedule_4_09, proposal.schedule_4_09_file)}</td>
          </tr>

          {/* Row 10a */}
          <tr>
            <td style={vpTdStyle} rowSpan={2}>१०</td>
            <td style={vpTdStyle}>	अ. संपादन करावयाच्या क्षेत्राचे ७/१२ चे उतारे किंवा मालमत्ता नोंदवहीतील उतारा प्रस्तावासोबत जोडणे आवश्यक आहे, त्याप्रमाणे जोडण्यात आले आहेत काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.extractsextracts_7_12_10, proposal.extractsextracts_7_12_10_file)}</td>
          </tr>

          {/* Row 10b */}
          <tr>
            <td style={vpTdStyle}>ब. संपादन करावयाच्या क्षेत्राचे ड्रोन‌द्वारे केलेले छायाचित्रण व KML फाईल जोडण्यात आले आहेत काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.drone_kml_file, proposal.drone_kml_file)}</td>
          </tr>

          {/* Row 11 */}
          <tr>
            <td style={vpTdStyle}>११</td>
            <td style={vpTdStyle}>	संपादन करावयाच्या क्षेत्राचे सीमांकन करण्यात आले आहे काय ? असल्यास, सीमांकन प्रमाणपत्र सोबत जोडले आहे काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.demarcation_11, proposal.demarcation_11_file)}</td>
          </tr>

          {/* Row 12 */}
          <tr>
            <td style={vpTdStyle}>१२</td>
            <td style={vpTdStyle}>	प्रस्तावामधील नमुद क्षेत्रासाठी यापूर्वीच थेट खरेदी / अन्य कायद्याच्या तरतुदीनुसार भूसंपादन प्रक्रिया सुरू करण्यात आलेली आहे काय ?(संपादनाबाबतचे प्रमाणपत्र)</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.acquisition_matter_12, proposal.acquisition_matter_12_file)}</td>
          </tr>

          {/* Row 13 */}
          <tr>
            <td style={vpTdStyle}>१३</td>
            <td style={vpTdStyle}>क्र.12 चे उत्तर - होय असल्यास, संबंधित भूसंपादन कायदा व प्रस्तावित क्षेत्र नमूद करावे.</td>
            <td style={vpTdStyle}><pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "inherit" }}>{proposal.land_acquisition_law_13 || "-"}</pre></td>
          </tr>

          {/* Row 14 */}
          <tr>
            <td style={vpTdStyle}>१४</td>
            <td style={vpTdStyle}>गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे काय ? त्याबाबत प्रमाणपत्र सोबत जोडावे.</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.taba_14, proposal.taba_14_file)}</td>
          </tr>

          {/* Row 15 */}
          <tr>
            <td style={vpTdStyle}>१५</td>
            <td style={vpTdStyle}>सबंधित प्रयोजनासाठी जवळपास कमी खर्चाची अन्य खाजगी किंवा सरकारी जमीन उपलब्ध आहे काय ? असल्यास, किती अंतरावर आहे ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.other_govt_land_15, proposal.other_govt_land_15_file)}</td>
          </tr>

          {/* Row 16 */}
          <tr>
            <td style={vpTdStyle}>१६</td>
            <td style={vpTdStyle}>	संपादन करून मागितलेली जमीन रेल्वे सीमांच्या जवळपास आहे काय? असल्यास, किती अंतरावर आहे ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.near_railway_boundaries_16, proposal.near_railway_boundaries_16_file)}</td>
          </tr>

          {/* Row 17a */}
          <tr>
            <td style={vpTdStyle} rowSpan={2}>१७</td>
            <td style={vpTdStyle}>अ.संपादन क्षेत्रात धार्मिक, प्रार्थना स्थळे, मंदिरे, कब्रस्थान व स्मशानभूमी किंवा पवित्र स्मारके येतात काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.religious_places_17, proposal.religious_places_17_file)}</td>
          </tr>

          {/* Row 17b */}
          <tr>
            <td style={vpTdStyle}>ब.संपादन क्षेत्राच्या जवळपास अशा बाबी असल्यास, किती अंतरावर आहेत ?</td>
            <td style={vpTdStyle}>{proposal.proximity_distance || "-"}</td>
          </tr>

          {/* Row 18 */}
          <tr>
            <td style={vpTdStyle}>१८</td>
            <td style={vpTdStyle}>	संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनिःसारण मार्ग जातो काय ? किंवा खास काही अडथळा निर्माण होतो काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.any_obstruction_18, proposal.any_obstruction_18_file)}</td>
          </tr>

          {/* Row 19 */}
          <tr>
            <td style={vpTdStyle}>१९</td>
            <td style={vpTdStyle}>नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाचे किंवा पूर्वापार प्रकाशमार्ग आणि / किंवा हायवेचे मार्ग यावरील कोणत्याही अधिकारात अडथळा येणेचा संभव आहे काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.road_obstruction_19, proposal.road_obstruction_19_file)}</td>
          </tr>

          {/* Row 20 */}
          <tr>
            <td style={vpTdStyle}>२०</td>
            <td style={vpTdStyle}>संपादन क्षेत्राच्या सभोवार इमारत किंवा कारखाना यांना किंवा त्यांची जागा छेदून जाते काय ?</td>
            <td style={vpTdStyle}>{renderYesNoWithFile(proposal.private_buildings_20, proposal.private_buildings_20_file)}</td>
          </tr>

          {/* Row 21 */}
          <tr>
            <td style={vpTdStyle}>२१</td>
            <td style={vpTdStyle}>प्रादेशिक विभाग/प्राधिकरण / महामंडळाच्या प्राधिकृत अधिकाऱ्याचा तपशील</td>
            <td style={vpTdStyle}><span className="font-bold">नाव :- </span>{proposal.acquisition_regional_incharge_name || "-"} <br />
              <span className="font-bold">पदनाम :- </span>{proposal.acquisition_regional_incharge_designation || "-"} <br />
              <span className="font-bold">पत्ता :- </span>{proposal.acquisition_regional_incharge_address || "-"} <br />
              <span className="font-bold">भ्रमणध्वनी :-</span> {proposal.acquisition_regional_incharge_contact || "-"} <br />
              <span className="font-bold">कार्यालयीन दुरध्वनी :-</span> {proposal.acquisition_regional_incharge_office_contact || "-"} <br />
              <span className="font-bold">ईमेल :-</span> {proposal.acquisition_regional_incharge_email || "-"} <br />
            </td>

          </tr>
        </tbody>
      </table>

      <SignatureBlock
        formattedDate={formattedDate}
        address={proposal?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
        mt={120}
      />
    </div>
  );
};

// Shared table cell style for ViewProposal
const vpTdStyle = {
  border: "1px solid black",
  padding: "8px 12px",
  verticalAlign: "top",
  fontSize: 12,
  lineHeight: 1.6,
};

// ─────────────────────────────────────────────
// Common Page Wrapper: heading + footer bands
// ─────────────────────────────────────────────
const PageWrapper = ({ title, children, proposalNo, projectName }) => (
  <div
    className="page-section"
    style={{
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 8,
      marginBottom: 32,
      overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    }}
  >

    {/* Content */}
    <div style={{ padding: "20px 24px" }}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────
// Anusuchi-1 (Parishisht-1 → Survey land table)
// ─────────────────────────────────────────────
const Anusuchi1Content = ({ data, proposalData, proposalNo, formattedDate, inchargeName, inchargeDesig, regionalName, regionalDesig }) => {
  
  const surveyTypes = ["सर्व्हे क्रमांक", "गट क्रमांक", "प्लॉट क्रमांक"];

  // Group data by District → Taluka → Village (3-level hierarchy)
  const groupedDistricts = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const distMap = {};
    data.forEach((item) => {
      const distKey = item.dist_name;
      const talukaKey = item.taluka_name;
      const villageKey = `${item.village_name}-${talukaKey}-${distKey}`;

      if (!distMap[distKey]) {
        distMap[distKey] = { dist_name: item.dist_name, talukas: {} };
      }
      const talukaMap = distMap[distKey].talukas;
      if (!talukaMap[talukaKey]) {
        talukaMap[talukaKey] = { taluka_name: item.taluka_name, villages: {} };
      }
      const villageMap = talukaMap[talukaKey].villages;
      if (!villageMap[villageKey]) {
        villageMap[villageKey] = {
          village_name: item.village_name,
          taluka_name: item.taluka_name,
          dist_name: item.dist_name,
          items: [],
        };
      }
      villageMap[villageKey].items.push(item);
    });

    return Object.values(distMap).map(dist => ({
      ...dist,
      talukas: Object.values(dist.talukas).map(taluka => ({
        ...taluka,
        villages: Object.values(taluka.villages),
      })),
    }));
  }, [data]);

  const surveyKramankData = (villageItems) => 
    villageItems.filter(item => surveyTypes.includes(item.survey_type));

  if (!data || data.length === 0) {
    return <p style={{ color: "#888", textAlign: "center" }}>नोंद आढळली नाही</p>;
  }

  return (
    <>
      <h3 className="center-text" style={{ fontWeight: 700, textAlign: "center" }}>परिशिष्ट - 1</h3>
      <h3 className="center-text" style={{ fontWeight: 700, textAlign: "center" }}>अनुसूची - 1</h3>
      <p style={{ textAlign: "center", fontWeight: 700 }}>प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनींचा तपशील</p>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रकल्प नाव: {proposalData?.project_name}
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रस्ताव नाव: {proposalData?.parishisht1_anusuchi1_header || proposalNo}
      </h3>

      {groupedDistricts.map((dist, dIdx) => (
        <div key={`dist-${dIdx}`}>
          {dist.talukas.map((taluka, tIdx) => (
            <div key={`taluka-${dIdx}-${tIdx}`}>
              {taluka.villages.map((village, vIdx) => {
        const villageSurveyData = surveyKramankData(village.items);

        return (
          <div key={`village-${dIdx}-${tIdx}-${vIdx}`} style={{ marginBottom: "40px", pageBreakInside: "avoid" }}>
            
            {/* Village Header */}
            <p style={{ 
              textAlign: "center", 
              fontWeight: "bold", 
              margin: "8px 0 10px 0", 
              fontSize: "15px" 
            }}>
              मौजे : {village.village_name} , तालुका : {village.taluka_name} , जिल्हा : {village.dist_name}
            </p>

            {villageSurveyData.length > 0 && (
              <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black", marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center", fontSize: "12px" }}>अ.क्र.</th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "left", minWidth: "160px" }}>भूधारकाचे नाव</th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center" }}>गट/सर्व्हे </th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center" }}>भूधारण पद्धती </th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center", maxWidth: "110px" }}>
                      7/12 प्रमाणे/अभिलेखाप्रमाणे एकुण क्षेत्र ({village.items[0]?.unit_name || 'हे.'})
                    </th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center" }}>हिस्सा क्षेत्र ({village.items[0]?.unit_name || 'हे.'})</th>
                    <th style={{ border: "1px solid black", padding: "6px", textAlign: "center" }}>संपादनाचे प्रस्तावित क्षेत्र ({village.items[0]?.unit_name || 'हे.'})</th>
                  </tr>
                </thead>
                <tbody>
                  {[...villageSurveyData]
                    .sort((a, b) => a.LandAcquisition_id - b.LandAcquisition_id)
                    .map((item, idx) => {
                      const hasOwners = item.owners && item.owners.length > 0;
                      const rowSpanValue = hasOwners ? item.owners.length : 1;

                      return hasOwners ? (
                        <React.Fragment key={idx}>
                          {item.owners.map((owner, ownerIdx) => {
                            const hasIndividualAqr = owner.owner_aqr_area && parseFloat(owner.owner_aqr_area) > 0;
                            const hasCommonArea = owner.common_area && parseFloat(owner.common_area) > 0;

                            const isFirstCommonAreaOwner = hasCommonArea &&
                              ownerIdx === item.owners.findIndex(o =>
                                o.common_area && parseFloat(o.common_area) > 0 &&
                                (!o.owner_aqr_area || parseFloat(o.owner_aqr_area) === 0)
                              );

                            return (
                              <tr key={`${idx}-owner-${ownerIdx}`}>
                                {ownerIdx === 0 && (
                                  <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                    {idx + 1}
                                  </td>
                                )}
                                <td style={{ border: "1px solid black", padding: "6px", textAlign: "left", whiteSpace: "pre-line" }}>
                                  {owner.owner_name?.split(",").map((name, i, arr) => 
                                    i < arr.length - 1 ? name.trim() + "," : name.trim()
                                  ).join("\n")}
                                </td>
                                {ownerIdx === 0 && (
                                  <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                    {item.survey_no}
                                  </td>
                                )}
                                {ownerIdx === 0 && (
                                  <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                    {item.land_holding_type || "-"}
                                  </td>
                                )}
                                {ownerIdx === 0 && (
                                  <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                    {parseFloat(item.total_7_12_area || item.total_area || 0).toFixed(2)}
                                  </td>
                                )}
                                <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                  {parseFloat(owner.owner_total_area || 0).toFixed(2)}
                                </td>
                                <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                  {hasIndividualAqr 
                                    ? parseFloat(owner.owner_aqr_area).toFixed(2) 
                                    : hasCommonArea && isFirstCommonAreaOwner 
                                      ? parseFloat(owner.common_area).toFixed(2) 
                                      : "0.00"}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ) : null;
                    })}

                  {/* Village Total Row */}
                  <tr>
                    <td colSpan={5} style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "right" }}>एकुण</td>
                    <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                      {villageSurveyData.reduce((sum, item) => 
                        sum + (item.owners?.reduce((oSum, o) => oSum + Number(o.owner_total_area || 0), 0) || 0), 0).toFixed(2)}
                    </td>
                    <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                      {villageSurveyData.reduce((sum, item) => 
                        sum + (item.owners?.reduce((oSum, o) => oSum + Number(o.owner_aqr_area || 0), 0) || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        );
              })}
            </div>
          ))}
        </div>
      ))}

      <SignatureBlock
        formattedDate={formattedDate}
        address={proposalData?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
      />
    </>
  );
};

// ─────────────────────────────────────────────
// Anusuchi-2 Summary (Parishisht-1 → land summary)
// ─────────────────────────────────────────────
const Anusuchi2SummaryContent = ({ data, proposalData, proposalNo, formattedDate, inchargeName, inchargeDesig, regionalName, regionalDesig }) => {

  // Group by District → Taluka → Village (same as Anusuchi1Content)
  const groupedDistricts = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const distMap = {};
    data.forEach((item) => {
      const distKey = item.dist_name;
      const talukaKey = item.taluka_name;
      const villageKey = `${item.village_name}-${talukaKey}-${distKey}`;
      if (!distMap[distKey]) distMap[distKey] = { dist_name: item.dist_name, talukas: {} };
      const talukaMap = distMap[distKey].talukas;
      if (!talukaMap[talukaKey]) talukaMap[talukaKey] = { taluka_name: item.taluka_name, villages: {} };
      const villageMap = talukaMap[talukaKey].villages;
      if (!villageMap[villageKey]) {
        villageMap[villageKey] = {
          village_name: item.village_name,
          taluka_name: item.taluka_name,
          dist_name: item.dist_name,
          unit_name: item.unit_name,
          items: [],
        };
      }
      villageMap[villageKey].items.push(item);
    });
    return Object.values(distMap).map(dist => ({
      ...dist,
      talukas: Object.values(dist.talukas).map(taluka => ({
        ...taluka,
        villages: Object.values(taluka.villages),
      })),
    }));
  }, [data]);

  if (!data || data.length === 0) return <p style={{ color: "#888", textAlign: "center" }}>नोंद आढळली नाही</p>;

  return (
    <>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>परिशिष्ट - 1</h3>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>अनुसूची - 2</h3>
      <p style={{ textAlign: "center", fontWeight: 700 }}>प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनीचा गोषवारा</p>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रकल्प नाव: {proposalData?.project_name}
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रस्ताव नाव: {proposalData?.parishisht1_anusuchi2_header || proposalNo}
      </h3>

      {groupedDistricts.map((dist, dIdx) => (
        <div key={`anusuchi2-dist-${dIdx}`}>

          {dist.talukas.map((taluka, tIdx) => (
            <div key={`anusuchi2-taluka-${dIdx}-${tIdx}`}>

              {taluka.villages.map((village, vIdx) => {
                const villageTotal = village.items.reduce((sum, item) =>
                  sum + (item.owners?.reduce((s, o) => s + (parseFloat(o.owner_aqr_area) || 0), 0) || 0), 0);

                const rows = [
                  { type: "खाजगी जमीन", area: villageTotal },
                  { type: "सरकारी जमीन", area: 0 },
                  { type: "केंद्र/राज्य शासनाच्या अखत्यारीतील सार्वजनिक उपक्रमाकडील जमीन", area: 0 },
                  { type: "केंद्र/राज्य शासनाच्या विभागाकडील जमीन", area: 0 },
                  { type: "वन जमीन", area: 0 },
                  { type: "स्थानिक स्वराज्य संस्थाकडील जमीन", area: 0 },
                  { type: "अन्य जमीन", area: 0 },
                ];

                return (
                  <div key={`anusuchi2-village-${dIdx}-${tIdx}-${vIdx}`} style={{ marginBottom: "30px" }}>
                    <p style={{ textAlign: "center", fontWeight: "bold", margin: "8px 0 10px 0", fontSize: "15px" }}>
                      मौजे : {village.village_name} , तालुका : {village.taluka_name} , जिल्हा : {village.dist_name}
                    </p>
                    <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black", marginTop: 10 }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>अ.क्र.</th>
                          <th style={thStyle}>जमिनीचा प्रकार</th>
                          <th style={thStyle}>भूसंपादन करावयाचे एकूण क्षेत्र ({village.unit_name || ''})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i}>
                            <td style={tdStyle}>{i + 1}</td>
                            <td style={{ ...tdStyle, textAlign: "left" }}>{row.type}</td>
                            <td style={tdStyle}>{Number(row.area).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={2} style={{ ...tdStyle, fontWeight: 700, textAlign: "right" }}>एकूण :</td>
                          <td style={{ ...tdStyle, fontWeight: 700 }}>{villageTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}

      <SignatureBlock
        formattedDate={formattedDate}
        address={proposalData?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
        mt={100}
      />
    </>
  );
};


// ─────────────────────────────────────────────
// Anusuchi-2 (घर क्रमांक – detailed)
// ─────────────────────────────────────────────
const Anusuchi2DetailContent = ({ data, proposalData, proposalNo, formattedDate, inchargeName, inchargeDesig, regionalName, regionalDesig }) => {
  const gharData = data.filter((i) => i.survey_type === "घर क्रमांक.");

  if (!gharData.length) return null;

  return (
    <>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>परिशिष्ट - 1 | अनुसूची - 2 (घर क्रमांक)</h3>
      <p style={{ textAlign: "center", fontWeight: 700 }}>प्रस्तावामध्ये समाविष्ट खाजगी जमिनींचा तपशील</p>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रकल्प नाव: {proposalData?.project_name} | प्रस्ताव नाव: {proposalNo}
      </h3>
      {data.length > 0 && (
        <p style={{ textAlign: "center", fontWeight: 700 }}>
          मौजे : {data[0].village_name} , तालुका : {data[0].taluka_name} , जिल्हा : {data[0].dist_name}
        </p>
      )}

      <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black", marginTop: 10 }}>
        <thead>
          <tr>
            {["अ.क्र.", "भूधारकाचे नाव", "घर क्रमांक", "भूधारण पद्धती",
              `हिस्सा क्षेत्र (${data[0]?.unit_name})`,
              `संपादनाचे प्रस्तावित क्षेत्र (${data[0]?.unit_name})`
            ].map((h, i) => (
              <th key={i} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gharData.map((item, idx) => {
            const owners = item.owners || [];
            const rowSpan = owners.length || 1;

            if (!owners.length) {
              return (
                <tr key={idx}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={{ ...tdStyle, textAlign: "left" }}>-</td>
                  <td style={tdStyle}>{item.survey_no}</td>
                  <td style={tdStyle}>{item.land_holding_type || "-"}</td>
                  <td style={tdStyle}>-</td>
                  <td style={tdStyle}>-</td>
                </tr>
              );
            }

            return (
              <React.Fragment key={idx}>
                {owners.map((owner, oIdx) => (
                  <tr key={`${idx}-${oIdx}`}>
                    {oIdx === 0 && <td rowSpan={rowSpan} style={{ ...tdStyle, verticalAlign: "middle" }}>{idx + 1}</td>}
                    <td style={{ ...tdStyle, textAlign: "left", whiteSpace: "pre-line" }}>
                      {owner.owner_name?.split(",").map((n, i, arr) => (i < arr.length - 1 ? n.trim() + "," : n.trim())).join("\n")}
                    </td>
                    {oIdx === 0 && <td rowSpan={rowSpan} style={{ ...tdStyle, verticalAlign: "middle" }}>{item.survey_no}</td>}
                    {oIdx === 0 && <td rowSpan={rowSpan} style={{ ...tdStyle, verticalAlign: "middle" }}>{item.land_holding_type || "-"}</td>}
                    <td style={tdStyle}>{parseFloat(owner.owner_total_area || 0).toFixed(2)}</td>
                    <td style={tdStyle}>{parseFloat(owner.owner_aqr_area || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          <tr>
            <td colSpan={4} style={{ ...tdStyle, fontWeight: 700, textAlign: "right" }}>एकुण</td>
            <td style={{ ...tdStyle, fontWeight: 700 }}>
              {gharData.reduce((sum, item) => sum + (item.owners?.reduce((s, o) => s + Number(o.owner_total_area || 0), 0) || 0), 0).toFixed(2)}
            </td>
            <td style={{ ...tdStyle, fontWeight: 700 }}>
              {gharData.reduce((sum, item) => sum + (item.owners?.reduce((s, o) => s + Number(o.owner_aqr_area || 0), 0) || 0), 0).toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>

      <SignatureBlock
        formattedDate={formattedDate}
        address={proposalData?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
        mt={150}
      />
    </>
  );
};

// Parishisht-2 Content (परिशिष्ट 2 - खाजगी जमिनीचा तपशील)
// ─────────────────────────────────────────────
const Parishisht2Content = ({ data, proposalData, proposalNo, formattedDate, inchargeName, inchargeDesig, regionalName, regionalDesig }) => {
  const surveyTypes = ["सर्व्हे क्रमांक", "गट क्रमांक", "प्लॉट क्रमांक"];
 
  // Group data by District → Taluka → Village
  const groupedDistricts = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const distMap = {};
    data.forEach((item) => {
      const distKey = item.dist_name;
      const talukaKey = item.taluka_name;
      const villageKey = `${item.village_name}-${talukaKey}-${distKey}`;
      if (!distMap[distKey]) distMap[distKey] = { dist_name: item.dist_name, talukas: {} };
      const talukaMap = distMap[distKey].talukas;
      if (!talukaMap[talukaKey]) talukaMap[talukaKey] = { taluka_name: item.taluka_name, villages: {} };
      const villageMap = talukaMap[talukaKey].villages;
      if (!villageMap[villageKey]) {
        villageMap[villageKey] = {
          village_name: item.village_name,
          taluka_name: item.taluka_name,
          dist_name: item.dist_name,
          items: [],
        };
      }
      villageMap[villageKey].items.push(item);
    });
    return Object.values(distMap).map(dist => ({
      ...dist,
      talukas: Object.values(dist.talukas).map(taluka => ({
        ...taluka,
        villages: Object.values(taluka.villages),
      })),
    }));
  }, [data]);
 
  if (!data || data.length === 0) {
    return <p style={{ color: "#888", textAlign: "center" }}>नोंद आढळली नाही</p>;
  }
 
  return (
    <>
      {/* Title */}
      <h3 style={{ textAlign: "center", fontWeight: 700, margin: 0, padding: 0 }}>परिशिष्ट 2</h3>
      <h3 style={{ textAlign: "center", fontWeight: 700, margin: "4px 0", padding: 0 }}>
        प्रकल्प नाव: {proposalData?.project_name}
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700, margin: "4px 0", padding: 0 }}>
        प्रस्ताव नाव: {proposalData?.parishisht2_header || proposalNo}
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700, margin: "4px 0", padding: 0 }}>
        प्रस्तावामध्ये समाविष्ट खाजगी जमिनीचा तपशील
      </h3>
 
      {/* District → Taluka → Village grouped tables */}
      {groupedDistricts.map((dist, dIdx) => (
        <div key={`p2-dist-${dIdx}`}>
          {dist.talukas.map((taluka, tIdx) => (
            <div key={`p2-taluka-${dIdx}-${tIdx}`}>
              {taluka.villages.map((village, vIdx) => {
                const surveyKramankData = village.items.filter(item => surveyTypes.includes(item.survey_type));
                const gharKramankData = village.items.filter(item => item.survey_type === "घर क्रमांक.");
 
                return (
                  <div key={`p2-village-${dIdx}-${tIdx}-${vIdx}`} style={{ marginTop: "20px" }}>
                    <p style={{ textAlign: "center", fontWeight: "bold", margin: "4px 0 8px 0", padding: 0 }}>
                      मौजे : {village.village_name} , तालुका : {village.taluka_name} , जिल्हा : {village.dist_name}
                    </p>
 
                    {/* Survey Data Table */}
                    {surveyKramankData.length > 0 && (
                      <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black" }}>
                        <thead>
                          <tr>
                            <th style={{ border: "1px solid black", padding: "6px" }}>अ.क्र.</th>
                            <th style={{ border: "1px solid black", padding: "6px", minWidth: "200px" }}>भूधारकाचे नाव</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>गट/सर्व्हे</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>भूधारणा पद्धती</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>7/12 प्रमाणे एकुण क्षेत्र</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>संपादनाचे प्रस्तावित क्षेत्र (हे. आर)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...surveyKramankData]
                            .sort((a, b) => a.LandAcquisition_id - b.LandAcquisition_id)
                            .map((item, idx) => {
                              const hasOwners = item.owners && item.owners.length > 0;
                              const rowSpanValue = hasOwners ? item.owners.length : 1;
                              return hasOwners ? (
                                <React.Fragment key={idx}>
                                  {item.owners.map((owner, ownerIdx) => (
                                    <tr key={`${idx}-owner-${ownerIdx}`}>
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {idx + 1}
                                        </td>
                                      )}
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "left", whiteSpace: "pre-line" }}>
                                        {owner.owner_name?.split(",").map((name, i, arr) => i < arr.length - 1 ? name.trim() + "," : name.trim()).join("\n")}
                                      </td>
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {item.survey_no}
                                        </td>
                                      )}
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {item.land_holding_type || "-"}
                                        </td>
                                      )}
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                        {parseFloat(owner.owner_total_area || 0).toFixed(2)}
                                      </td>
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                        {parseFloat(owner.owner_aqr_area || 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ) : null;
                            })}
                          <tr>
                            <td colSpan={4} style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "right" }}>एकुण</td>
                            <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                              {surveyKramankData.reduce((sum, item) =>
                                sum + (item.owners?.reduce((s, o) => s + Number(o.owner_total_area || 0), 0) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                              {surveyKramankData.reduce((sum, item) =>
                                sum + (item.owners?.reduce((s, o) => s + Number(o.owner_aqr_area || 0), 0) || 0), 0).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
 
                    {/* Ghar Kramank Table */}
                    {gharKramankData.length > 0 && (
                      <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black", marginTop: "16px" }}>
                        <thead>
                          <tr>
                            <th style={{ border: "1px solid black", padding: "6px" }}>अ.क्र.</th>
                            <th style={{ border: "1px solid black", padding: "6px", minWidth: "200px" }}>घर मालकाचे नाव</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>घर क्रमांक</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>भूधारणा पद्धती</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>7/12 प्रमाणे एकुण क्षेत्र</th>
                            <th style={{ border: "1px solid black", padding: "6px" }}>संपादनाचे प्रस्तावित क्षेत्र (चौरस फूट)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...gharKramankData]
                            .sort((a, b) => a.LandAcquisition_id - b.LandAcquisition_id)
                            .map((item, idx) => {
                              const hasOwners = item.owners && item.owners.length > 0;
                              const rowSpanValue = hasOwners ? item.owners.length : 1;
                              return hasOwners ? (
                                <React.Fragment key={idx}>
                                  {item.owners.map((owner, ownerIdx) => (
                                    <tr key={`${idx}-owner-${ownerIdx}`}>
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {idx + 1}
                                        </td>
                                      )}
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "left", whiteSpace: "pre-line" }}>
                                        {owner.owner_name?.split(",").map((name, i, arr) => i < arr.length - 1 ? name.trim() + "," : name.trim()).join("\n")}
                                      </td>
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {item.survey_no}
                                        </td>
                                      )}
                                      {ownerIdx === 0 && (
                                        <td rowSpan={rowSpanValue} style={{ border: "1px solid black", padding: "6px", verticalAlign: "middle", textAlign: "center" }}>
                                          {item.land_holding_type || "-"}
                                        </td>
                                      )}
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                        {parseFloat(owner.owner_total_area || 0).toFixed(2)}
                                      </td>
                                      <td style={{ border: "1px solid black", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
                                        {parseFloat(owner.owner_aqr_area || 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ) : null;
                            })}
                          <tr>
                            <td colSpan={4} style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "right" }}>एकुण</td>
                            <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                              {gharKramankData.reduce((sum, item) =>
                                sum + (item.owners?.reduce((s, o) => s + Number(o.owner_total_area || 0), 0) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: "1px solid black", padding: "6px", fontWeight: "bold", textAlign: "center" }}>
                              {gharKramankData.reduce((sum, item) =>
                                sum + (item.owners?.reduce((s, o) => s + Number(o.owner_aqr_area || 0), 0) || 0), 0).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
 
      <SignatureBlock
        formattedDate={formattedDate}
        address={proposalData?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
      />
    </>
  );
};

// ─────────────────────────────────────────────
// Anusuchi-3 / Parishisht-3 (Demarcation Certificate)
// ─────────────────────────────────────────────
const Anusuchi3Content = ({ formData }) => {
  return (
    <div style={{ fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif" }}>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>परिशिष्ट - 3</h3>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रकल्प नाव: {formData.project_name} 
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700 }}>
        प्रस्ताव नाव: {formData.proposal_no_name}
      </h3>
      <p style={{ textAlign: "center", fontWeight: 700 }}>
        मौजे : {formData.village} , तालुका : {formData.taluka} ,  जिल्हा : {formData.district}
      </p>

      <div style={{ textAlign: "center", fontWeight: 700, fontSize: 18, textDecoration: "underline", margin: "20px 0 16px" }}>
        प्रमाणपत्र
      </div>

      <div style={{ textAlign: "justify", lineHeight: 2, fontSize: 14 }}>
        <p>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;प्रमाणित करण्यात येते की, {formData.project_name} या प्रकल्पाच्या आखणीमध्ये समाविष्ट
          असलेल्या प्रस्तावित जमिनीचे सीमांकन करण्यात आले आहे. प्रस्तावित जमिनीचा ताबा खाजगी वाटाघाटीने
          किंवा इतर कोणत्याही मार्गाने घेतलेला नाही.
        </p>
        <p>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;प्रकल्पाच्या आखणीमध्ये समाविष्ट वन जमिनी वन विभागाकडून हस्तांतरीत करण्याबाबत आणि त्याप्रमाणे
          आवश्यक कार्यवाही प्रादेशिक विभाग/प्राधिकरण/महामंडळाच्या वतीने स्वतंत्ररीत्या कार्यान्वित करण्यात येत आहे.
        </p>
        <p>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;प्रकल्पाच्या आखणीमध्ये समाविष्ट शासकीय जमिनी /इतर विभागांच्या जमिनी महामंडळास हस्तांतरीत
          करण्याबाबत आणि त्याप्रमाणे आवश्यक कार्यवाही प्रादेशिक विभाग/प्राधिकरण/महामंडळाच्या वतीने
          स्वतंत्ररीत्या कार्यान्वित करण्यात येत आहे.
        </p>
        <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;सदरचा प्रस्ताव यापूर्वी प्रादेशिक विभाग/प्राधिकरण/महामंडळामार्फत दाखल केलेला नाही.</p>
      </div>

      <div style={{ marginTop: 32 }}>
        <div>दिनांक :- <span>{formatDate(formData.date || new Date().toISOString())}</span></div>
        <div style={{ marginTop: 6 }}>ठिकाण :- <span>{formData.place}</span></div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-around", marginTop: 60 }}>
        <div style={{ textAlign: "center" }}>
          <div>({formData.officer1_name})</div>
          <div>{formData.officer1_designation}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div>({formData.officer2_name})</div>
          <div>{formData.officer2_designation}</div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Shared Signature Block
// ─────────────────────────────────────────────
const SignatureBlock = ({ formattedDate, address, inchargeName, inchargeDesig, regionalName, regionalDesig, mt = 100 }) => (
  <>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
      <div style={{ textAlign: "left" }}>
        <p>दिनांक: {formattedDate}</p>
        <p>ठिकाण: {address}</p>
      </div>
    </div>
    <div style={{ display: "flex", justifyContent: "space-around", marginTop: 10 }}>
      <div style={{ textAlign: "center", marginTop: mt, lineHeight: 1.5 }}>
        {regionalName && <p>({regionalName})</p>}
        {regionalDesig && <p>{regionalDesig}</p>}
      </div>
      <div style={{ textAlign: "center", marginTop: mt, lineHeight: 1.5 }}>
        {inchargeName && <p>({inchargeName})</p>}
        {inchargeDesig && <p>{inchargeDesig}</p>}
      </div>
    </div>
  </>
);

// ─────────────────────────────────────────────
// Parishisht-6 (Boundary / Chatuhsima Table)
// ─────────────────────────────────────────────
const Parishisht6Content = ({
  data,
  proposalData,
  proposalNo,
  projectName,
  locationInfo,
  formattedDate,
  inchargeName,
  inchargeDesig,
  regionalName,
  regionalDesig,
  inchargeAddress,
}) => {
  const surveyTypes = ["सर्व्हे क्रमांक", "गट क्रमांक", "प्लॉट क्रमांक"];
 
  const boundaryThStyle = {
    border: "1px solid black",
    padding: "8px",
    textAlign: "center",
    background: "#f3f4f6",
    fontWeight: 700,
    fontSize: 12,
  };
  const boundaryTdStyle = {
    border: "1px solid black",
    padding: "6px 8px",
    textAlign: "center",
    fontSize: 12,
  };
 
  const BoundaryTable = ({ rows, label }) => {
    if (!rows.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black" }}>
          <thead>
            <tr>
              <th rowSpan={2} style={boundaryThStyle}>अ.<br />क्र.</th>
              <th rowSpan={2} style={boundaryThStyle}>{label}</th>
              <th colSpan={4} style={boundaryThStyle}>
                भूसंपादन क्षेत्राच्या चतुःसिमेस असलेले {label}
              </th>
            </tr>
            <tr>
              {["पूर्व", "पश्चिम", "उत्तर", "दक्षिण"].map((dir) => (
                <th key={dir} style={boundaryThStyle}>{dir}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
                <td style={boundaryTdStyle}>{idx + 1}</td>
                <td style={boundaryTdStyle}>{row.survey_no || "-"}</td>
                <td style={boundaryTdStyle}>{row.east || "-"}</td>
                <td style={boundaryTdStyle}>{row.west || "-"}</td>
                <td style={boundaryTdStyle}>{row.north || "-"}</td>
                <td style={boundaryTdStyle}>{row.south || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
 
  // Group data by District → Taluka → Village (same as Anusuchi1 & Anusuchi2)
  const groupedDistricts = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const distMap = {};
    data.forEach((item) => {
      const distKey = item.dist_name;
      const talukaKey = item.taluka_name;
      const villageKey = `${item.village_name}-${talukaKey}-${distKey}`;
      if (!distMap[distKey]) distMap[distKey] = { dist_name: item.dist_name, talukas: {} };
      const talukaMap = distMap[distKey].talukas;
      if (!talukaMap[talukaKey]) talukaMap[talukaKey] = { taluka_name: item.taluka_name, villages: {} };
      const villageMap = talukaMap[talukaKey].villages;
      if (!villageMap[villageKey]) {
        villageMap[villageKey] = {
          village_name: item.village_name,
          taluka_name: item.taluka_name,
          dist_name: item.dist_name,
          items: [],
        };
      }
      villageMap[villageKey].items.push(item);
    });
    return Object.values(distMap).map(dist => ({
      ...dist,
      talukas: Object.values(dist.talukas).map(taluka => ({
        ...taluka,
        villages: Object.values(taluka.villages),
      })),
    }));
  }, [data]);
 
  if (!data || data.length === 0) {
    return <p style={{ textAlign: "center", color: "#888" }}>चतुःसिमा डेटा आढळला नाही</p>;
  }
 
  return (
    <>
      {/* Document headings */}
      <h3 style={{ textAlign: "center", fontWeight: 700, marginBottom: 2 }}>परिशिष्ट - 6</h3>
      <h3 style={{ textAlign: "center", fontWeight: 700, marginBottom: 2 }}>
        प्रकल्प नाव: {projectName || proposalData?.project_name}
      </h3>
      <h3 style={{ textAlign: "center", fontWeight: 700, marginBottom: 2 }}>
        प्रस्ताव नाव: {proposalData?.parishisht6_header || proposalNo}
      </h3>
      <h2
        style={{
          textAlign: "center",
          fontSize: 15,
          fontWeight: 600,
          textDecoration: "underline",
          marginBottom: 16,
        }}
      >
        प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनीच्या चतु:सिमा दर्शविणारा तक्ता
      </h2>
 
      {/* District → Taluka → Village grouped boundary tables */}
      {groupedDistricts.map((dist, dIdx) => (
        <div key={`p6-dist-${dIdx}`}>
          {dist.talukas.map((taluka, tIdx) => (
            <div key={`p6-taluka-${dIdx}-${tIdx}`}>
              {taluka.villages.map((village, vIdx) => {
                const villageSurveyRows = village.items.filter(i => surveyTypes.includes(i.survey_type));
                const villageGharRows = village.items.filter(i => i.survey_type === "घर क्रमांक.");
 
                if (!villageSurveyRows.length && !villageGharRows.length) return null;
 
                return (
                  <div key={`p6-village-${dIdx}-${tIdx}-${vIdx}`} style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                    <p style={{ textAlign: "center", fontWeight: "bold", margin: "8px 0 12px 0", fontSize: "15px" }}>
                      मौजे : {village.village_name} , तालुका : {village.taluka_name} , जिल्हा : {village.dist_name}
                    </p>
                    <BoundaryTable rows={villageSurveyRows} label="गट/सर्व्हे क्रमांक" />
                    <BoundaryTable rows={villageGharRows} label="घर क्रमांक" />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
 
      {/* Signature block */}
      <SignatureBlock
        formattedDate={formattedDate}
        address={inchargeAddress || proposalData?.acquisition_incharge_address}
        inchargeName={inchargeName}
        inchargeDesig={inchargeDesig}
        regionalName={regionalName}
        regionalDesig={regionalDesig}
        mt={150}
      />
    </>
  );
};

const NoPreviousAcquisitionCertificate = ({ formData }) => {
  if (!formData) return null;

  return (
    <div
      className="certificate p-6 mb-8"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Project & Proposal info at top — same as OtherDocuments */}
      <div className="flex justify-between items-center mb-6 font-semibold" style={{ fontSize: 14 }}>
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>

      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-8"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        पूर्वी भूसंपादन न झाल्याबाबत प्रमाणपत्र
      </div>

      {/* Certificate Body */}
      <div
        className="certificate-content text-justify leading-8"
        style={{ fontSize: 14, lineHeight: 2.2 }}
      >
        <p>प्रमाणित करण्यात येते की, <span className="underline-field">{formData.project_name}</span> या प्रकल्पांतर्गत येणाऱ्या मौजे. प्रस्ताव नाव:<span></span> <span className="underline-field">{formData.village}</span> ता.<span className="underline-field">{formData.taluka}</span> जि.<span className="underline-field">{formData.district}</span> येथील भूसंपादित करावयाच्या जमिनीचा प्रत्यक्षात खाजगी वाटाघाटीद्वारे ताबा घेतलेला नाही. तसेच प्रस्तावीत क्षेत्र यापूर्वी इतर कोणत्याही प्रयोजनासाठी/प्रकल्पासाठी संपादीत करण्यात आलेले नाही.
        </p>
      </div>

      {/* Signatures — same layout & spacing as OtherDocuments */}
      <div
        className="signature-section flex justify-between mt-20"
        style={{ marginTop: "80px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "220px",
              margin: "0 auto 8px",
              minHeight: "25px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "220px",
              margin: "0 auto 8px",
              minHeight: "25px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
const ProposalNotCancelledCertificate = ({ formData }) => {
  if (!formData) return null;

  return (
    <div
      className="certificate p-6 mb-8"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Top row: Project name | Proposal number */}
      <div className="flex justify-between items-center mb-6 font-semibold" style={{ fontSize: 14 }}>
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>

      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-8"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        प्रस्ताव रद्द होणार नसल्याचे प्रमाणपत्र
      </div>

      {/* Certificate Body */}
      <div
        className="certificate-content text-justify leading-8"
        style={{ fontSize: 14, lineHeight: 2.2 }}
      >
        <p>
          प्रमाणित करण्यात येते की, <span className="underline-field">{formData.project_name}</span> या प्रकल्पांतर्गत येणाऱ्या मौजे <span className="underline-field">{formData.village}</span> ता.<span className="underline-field">{formData.taluka}</span> जि.<span className="underline-field">{formData.district}</span> येथील <span className="underline-field">{formData.proposal_no_name}</span> भूसंपादन प्रस्ताव या कार्यालयाकडून भविष्यात रद्द होणार नाही.
        </p>

      </div>

      {/* Signatures */}
      <div
        className="signature-section flex justify-between mt-20"
        style={{ marginTop: "80px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "220px",
              margin: "0 auto 8px",
              minHeight: "25px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "220px",
              margin: "0 auto 8px",
              minHeight: "25px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>

      </div>
    </div>
  );
};

const SurveyFeeCertificate = ({ formData }) => {
  if (!formData) return null;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Project & Proposal info at top — same as OtherDocuments */}
      <div className="flex justify-between items-center mb-6 font-semibold" style={{ fontSize: 14 }}>
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title – centered, underlined */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-10"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        संयुक्त मोजणी शुल्क शासन जमा करणे बाबत प्रमाणपत्र
      </div>

      {/* Certificate Body */}
      <div
        className="certificate-content text-justify leading-8"
        style={{ fontSize: 14, lineHeight: 2.2 }}
      >
        <p>
          प्रमाणित करण्यात येते की, <span className="underline-field">{formData.project_name}</span> या प्रकल्पांतर्गत येणाऱ्या मौजे <span className="underline-field">{formData.village}</span> ता.<span className="underline-field">{formData.taluka}</span> जि.<span className="underline-field">{formData.district}</span> येथील <span className="underline-field">{formData.proposal_no_name}</span> भूसंपादन प्रस्तावाच्या अनुषंगाने भूसंपादन करिता संयुक्त मोजणी कामाकरिता शासन निर्णयानुसार व भूमिअभिलेख विभागाने कळविल्यानुसार मोजणी शुल्काची रक्कम या कार्यालयाकडून शासन जमा करण्यात येईल.
        </p>

      </div>

      {/* Signatures – generous top margin to look balanced with header */}
      <div
        className="signature-section flex justify-between mt-24"
        style={{ marginTop: "100px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>

      </div>
    </div>
  );
};

const FinancialProvisionCertificate = ({ formData }) => {
  if (!formData) return null;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      <div className="flex justify-between items-center mb-6 font-semibold" style={{ fontSize: 14 }}>
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title – centered, underlined */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-10"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        निधी उपलब्धता प्रमाणपत्र
      </div>

      {/* Certificate Body */}
      <div
        className="certificate-content text-justify leading-8"
        style={{ fontSize: 14, lineHeight: 2.2 }}
      >
        <p>
          {formData.financial_certificate_text ||
            `प्रमाणित करण्यात येते की, ${formData.project_name} या प्रकल्पास शासकीय मान्यता प्राप्त असून शासनाकडून/विभागाकडून सदर प्रकल्पाकरिता रक्कम ${formData.amount}/- ची आर्थिक तरतूद करण्यात आलेली आहे.`}
        </p>
      </div>

      {/* Signatures – generous spacing */}
      <div
        className="signature-section flex justify-between mt-24"
        style={{ marginTop: "100px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>

      </div>
    </div>
  );
};

const PublicPurposeDetailsCertificate = ({ formData }) => {
  if (!formData) return null;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >

      {/* Top row – project & proposal (optional – can remove if PageWrapper already shows it) */}
      <div className="flex justify-between items-center mb-6 font-semibold text-sm">
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-8"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        सार्वजनिक प्रयोजनाच्या स्वरुपाबाबत विवरण
      </div>

      {/* Table – read-only version */}
      <table
        className="w-full border-collapse border border-black text-sm"
        style={{ fontSize: 13, lineHeight: 1.6 }}
      >
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-black p-3 font-bold text-center w-10">अ.क्र.</th>
            <th className="border border-black p-3 font-bold text-center w-3/12">मुद्दे</th>
            <th className="border border-black p-3 font-bold text-center">तपशील</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black p-3 text-center">1</td>
            <td className="border border-black p-3">प्रकल्प नाव</td>
            <td className="border border-black p-3 text-left">{formData.project_name || "-"}</td>
          </tr>

          <tr>
            <td className="border border-black p-3 text-center">2</td>
            <td className="border border-black p-3">प्रकल्प कार्याचे वर्णन</td>
            <td className="border border-black p-3 text-left whitespace-pre-wrap break-words">
              {formData.project_description || "तपशील उपलब्ध नाही"}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-3 text-center">3</td>
            <td className="border border-black p-3">समाजाला मिळणारे लाभ</td>
            <td className="border border-black p-3 text-left whitespace-pre-wrap break-words">
              {formData.project_social_benfit || "तपशील उपलब्ध नाही"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Signatures */}
      <div
        className="signature-section flex justify-between mt-16"
        style={{ marginTop: "80px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>
      </div>
    </div>
  );
};

const ReligiousPlacesCertificate = ({ formData }) => {
  if (!formData) return null;

  const defaultText = `प्रमाणित करण्यात येते की, ${formData.project_name || "N/A"} या प्रकल्पांतर्गत येणाऱ्या मौजे ${formData.village || "N/A"} ता. ${formData.taluka || "N/A"} जि. ${formData.district || "N/A"} येथील भूसंपादित करावयाच्या जमिनीवर कोणत्याही प्रकारचे धार्मिक प्रार्थना स्थळे, मंदिरे, कब्रस्थान, स्मशानभूमी किंवा पवित्र स्मारके नाहीत.`;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Project & Proposal info (optional redundancy if PageWrapper already shows it) */}
      <div className="flex justify-between items-center mb-8 font-semibold text-sm">
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-10"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        धार्मिक स्थळांबाबत प्रमाणपत्र
      </div>

      {/* Certificate Content */}
      <div
        className="certificate-content text-justify leading-9"
        style={{ fontSize: 14, lineHeight: 2.1 }}
      >
        <p>
          {formData.religious_certificate_text ||
            `प्रमाणित करण्यात येते की,${formData.project_name} या प्रकल्पांतर्गत येणाऱ्या मौजे ${formData.village} ता.${formData.taluka} जि.${formData.district} येथील भूसंपादित करावयाच्या जमिनीवर कोणत्याही प्रकारचे धार्मिक प्रार्थना स्थळे, मंदिरे, कब्रस्थान, स्मशानभूमी किंवा पवित्र स्मारके नाहीत.`}
        </p>
      </div>

      {/* Signatures */}
      <div
        className="signature-section flex justify-between mt-20"
        style={{ marginTop: "100px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>

      </div>
    </div>
  );
};

const RailwayBoundaryCertificate = ({ formData }) => {
  if (!formData) return null;

  const defaultText = `प्रमाणित करण्यात येते की, ${formData.project_name || "N/A"} या प्रकल्पांतर्गत येणाऱ्या मौजे ${formData.village || "N/A"} ता. ${formData.taluka || "N/A"} जि. ${formData.district || "N/A"} येथील भूसंपादित करावयाच्या जमिनी रेल्वे सीमांच्या जवळ आहे/नाही. रेल्वे सीमांच्या जवळ असल्यास रेल्वे सीमेपासून अंतर ___ किमी आहे.`;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Project & Proposal info (optional – PageWrapper already shows this) */}
      <div className="flex justify-between items-center mb-8 font-semibold text-sm">
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-10"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        रेल्वे सीमा बाबत प्रमाणपत्र
      </div>

      {/* Certificate Content */}
      <div
        className="certificate-content text-justify leading-9"
        style={{ fontSize: 14, lineHeight: 2.1 }}
      >
        <p>
          {formData.railway_certificate_text ||
            `प्रमाणित करण्यात येते की,${formData.project_name} या प्रकल्पांतर्गत येणाऱ्या मौजे ${formData.village} ता.${formData.taluka} जि.${formData.district} येथील भूसंपादित करावयाच्या जमिनी रेल्वे सीमांच्या जवळ आहे/नाही. रेल्वे सीमांच्या जवळ असल्यास रेल्वे सीमेपासून अंतर किमी आहे.`}
        </p>
      </div>

      {/* Signatures */}
      <div
        className="signature-section flex justify-between mt-20"
        style={{ marginTop: "100px" }}
      >
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>

      </div>
    </div>
  );
};

const PublicRightsOfWayCertificate = ({ formData }) => {
  if (!formData) return null;

  const defaultText = `प्रमाणित करण्यात येते की, ${formData.project_name || "N/A"} या प्रकल्पांतर्गत येणाऱ्या मौजे ${formData.village || "N/A"} ता. ${formData.taluka || "N/A"} जि. ${formData.district || "N/A"} येथील भूसंपादित करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनि:सारण मार्ग जात आहे/नाही.`;

  return (
    <div
      className="certificate p-6"
      style={{
        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
        background: "white",
        maxWidth: "none"
      }}
    >
      {/* Project & Proposal info (optional – PageWrapper already displays this) */}
      <div className="flex justify-between items-center mb-8 font-semibold text-sm">
        <div>प्रकल्प नाव: {formData.project_name || "N/A"}</div>
        <div>प्रस्ताव नाव: {formData.proposal_no_name || "N/A"}</div>
      </div>
      {/* Main Title */}
      <div
        className="certificate-header text-xl font-bold text-center underline mb-10"
        style={{ textDecoration: "underline", fontSize: 18 }}
      >
        सार्वजनिक मार्गाधिकार प्रमाणपत्र
      </div>

      {/* Certificate Content */}
      <div
        className="certificate-content text-justify leading-9"
        style={{ fontSize: 14, lineHeight: 2.1 }}
      >
        <p>
          {formData.public_rights_certificate_text ||
            `प्रमाणित करण्यात येते की,${formData.project_name} अंतर्गत येणाऱ्या मौजे ${formData.village} ता.${formData.taluka} जि.${formData.district} येथील भूसंपादित करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनि:सारण मार्ग जात आहे/नाही.`}
        </p>
      </div>

      {/* Signatures */}
      <div
        className="signature-section flex justify-between mt-20"
        style={{ marginTop: "100px" }}
      ><div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer1_name || "N/A"})
          </div>
          <div>{formData.officer1_designation || "-"}</div>
        </div>
        <div className="signature-block text-center" style={{ width: "45%" }}>
          <div
            style={{
              width: "240px",
              margin: "0 auto 10px",
              minHeight: "28px"
            }}
          >
            ({formData.officer2_name || "N/A"})
          </div>
          <div>{formData.officer2_designation || "-"}</div>
        </div>
      </div>
    </div>
  );
};

// Shared td/th styles
// ─────────────────────────────────────────────
const tdStyle = {
  border: "1px solid black",
  padding: 6,
  textAlign: "center",
  verticalAlign: "top",
  fontSize: 12,
};
const thStyle = {
  border: "1px solid black",
  padding: 6,
  textAlign: "center",
  fontSize: 12,
  background: "#f9fafb",
};

// ─────────────────────────────────────────────────────────
// ✅  MAIN COMPONENT: LandholdingAllDocu
// ─────────────────────────────────────────────────────────
const LandholdingAllDocu = () => {
  const allDocRef = useRef(null);

  // ── Shared state ──
  const [landData, setLandData] = useState([]);
  const [viewProposalData, setViewProposalData] = useState(null); // for नमुना "क"
  const [proposalNo, setProposalNo] = useState("");
  const [proposalData, setProposalData] = useState(null);
  const [formData, setFormData] = useState(null); // for Anusuchi-3
  const [inchargeName, setInchargeName] = useState("");
  const [inchargeDesig, setInchargeDesig] = useState("");
  const [inchargeAddress, setInchargeAddress] = useState(""); // for Parishisht-6 & others
  const [regionalName, setRegionalName] = useState("");
  const [regionalDesig, setRegionalDesig] = useState("");
  const [locationInfo, setLocationInfo] = useState({ village: "", taluka: "", district: "" }); // for Parishisht-6

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Document viewer modal state
  const [showDocModal, setShowDocModal] = useState(false);
  const [docUrl, setDocUrl] = useState("");

  // Parishisht-4 / अनुसूची-4 states
  const [parishisht4File, setParishisht4File] = useState(null);
  const [existingParishisht4, setExistingParishisht4] = useState(null);
  const [uploadingP4, setUploadingP4] = useState(false);
  const [successP4, setSuccessP4] = useState(false);
  const [errorP4, setErrorP4] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);

  // Document viewer (can reuse the same modal states if already present, or add new ones)
  const [showDocViewerP4, setShowDocViewerP4] = useState(false);
  const [docUrlP4, setDocUrlP4] = useState('');
  const [isPdfP4, setIsPdfP4] = useState(false);

  // Digital signature
    const { uploadStatus, uploadError, selectedSignDocument, resetSignatureState, signDocument } = useSignature();
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);

  const gharData = landData.filter(i => i.survey_type === "घर क्रमांक.");

  // ── Derived helpers ──
  const formattedDate = todayFormatted;
  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("userData")); } catch { return {}; }
  })();

  const userRole = (userData?.role || userData?.data?.role || userData?.user_type || userData?.data?.user_type || "").toLowerCase();
  const isViewOnlyRole = ["collector", "samanvay"].some(r => userRole.includes(r));

  // ── Data Fetching ── (single fetch for all three documents)
  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Read from localStorage
      const selectedProject = (() => {
        try { return JSON.parse(localStorage.getItem("selectedProject") || "{}"); } catch { return {}; }
      })();
      const storedProposal = (() => {
        try { return JSON.parse(localStorage.getItem("selectedProposal") || "{}"); } catch { return {}; }
      })();
      const storedLocationInfo = (() => {
        try {
          const raw = localStorage.getItem("selectedLocationInfo");
          return raw ? JSON.parse(raw) : null;
        } catch { return null; }
      })();

      const currentProjectId = projectId || selectedProject?.id || selectedProject?.project_id;
      const proposalId =
        localStorage.getItem("currentProposalId") ||
        findProposalId(selectedProject) ||
        userData?.default_proposal ||
        userData?.proposal_id;

      if (!currentProjectId || !proposalId) {
        setError("Project ID या Proposal ID मिळाला नाही. कृपया प्रथम प्रकल्प निवडा.");
        setLoading(false);
        return;
      }

      // 2. Set officer info from storedProposal
      setRegionalName(storedProposal?.acquisition_regional_incharge_name || "");
      setRegionalDesig(storedProposal?.acquisition_regional_incharge_designation || "");
      setInchargeName(storedProposal?.acquisition_incharge_name || "");
      setInchargeDesig(storedProposal?.acquisition_incharge_designation || "");

      // 3. Build API params
      let apiParams = { proposalId };
      

      // 4. Fetch land holdings (used by Anusuchi-1 & 2)
      const result = await apiService.getLandHolding(currentProjectId, apiParams);

      if (!result || result === "" || result === null) {
        setError("API ने अवैध प्रतिसाद दिला. Network तपासा.");
        return;
      }

      const responseStatus = result?.status || result?.statusCode || 200;

      if (responseStatus === 200 || responseStatus === "200") {
        // Extract proposal info
        if (Array.isArray(result.proposalData) && result.proposalData.length > 0) {
          const pInfo = result.proposalData[0];
          setProposalNo(pInfo.acquisition_proposal_no);
          setProposalData(pInfo);
        }

        // Extract land data
        let dataArray =
          Array.isArray(result.data) ? result.data :
            Array.isArray(result.data?.data) ? result.data.data :
              Array.isArray(result.data?.items) ? result.data.items :
                Array.isArray(result.data?.records) ? result.data.records :
                  Array.isArray(result.data?.landHoldings) ? result.data.landHoldings : null;

        if (dataArray?.length) {
          setLandData(dataArray.map((item, i) => ({ ...item, sr_no: i + 1 })));

          // ── Populate locationInfo for Parishisht-6 ──
          const first = dataArray[0];
          const resolvedLocation = {
            village: first.village_name || first.VillageName || storedLocationInfo?.village || "",
            taluka: first.tehsil_name || first.taluka_name || storedLocationInfo?.taluka || "",
            district: first.dist_name || storedLocationInfo?.district || "",
          };
          setLocationInfo(resolvedLocation);

          // Cache location info to localStorage if not already there
          if (!storedLocationInfo) {
            localStorage.setItem(
              "selectedLocationInfo",
              JSON.stringify({
                projectId: first.project_id || currentProjectId,
                ...resolvedLocation,
              })
            );
          }
        } else {
          setError("API प्रतिसाद यशस्वी परंतु कोणताही डेटा मिळाला नाही.");
        }
      } else {
        setError(`API ने स्थिती ${responseStatus} परत केली: ${result?.message || "अज्ञात त्रुटी"}`);
      }

      // 5. Fetch proposal detail for Anusuchi-3 AND ViewProposal (नमुना "क")
      try {
        const propResult = await apiService.getProposalById(currentProjectId, proposalId);

        console.log("Proposal in All Doc" , propResult);
        
        if (propResult?.status === 200) {
          const proposal = Array.isArray(propResult.data) ? propResult.data[0] : propResult.data;

          // ── Set viewProposalData for नमुना "क" section ──
          setViewProposalData(proposal);

          // Override officer info with more accurate source
          setRegionalName(proposal?.acquisition_regional_incharge_name || "");
          setRegionalDesig(proposal?.acquisition_regional_incharge_designation || "");
          setInchargeName(proposal?.acquisition_incharge_name || "");
          setInchargeDesig(proposal?.acquisition_incharge_designation || "");
          setInchargeAddress(proposal?.acquisition_incharge_address || ""); // ← for Parishisht-6

          // ── Extract village, taluka, and district from the dtv array ──
    const dtvList = proposal?.dtv || [];
    
    // Map through the array to get all village names and join them with a comma
    const joinedVillages = dtvList.map(item => item.village_name).filter(Boolean).join(", ");
    
    // Pick the district and taluka from the first object (since they are mostly the same)
    const firstDistrict = dtvList[0]?.dist_name || proposal?.dist_name || "";
    const firstTaluka = dtvList[0]?.taluka_name || proposal?.taluka_name || "";

          setFormData({
            project_name: selectedProject.project_name || "",
            village: joinedVillages, // e.g., "निमगांव, रामपूर"
      taluka: firstTaluka,     // Extracted from dtv[0]
      district: firstDistrict, // Extracted from dtv[0]
            proposal_no_name: proposal?.acquisition_proposal_no || "",
            amount: proposal?.acquisition_area || "",
            officer1_name: proposal?.acquisition_regional_incharge_name || "",
            officer1_designation: proposal?.acquisition_regional_incharge_designation || "",
            officer2_name: proposal?.acquisition_incharge_name || "",
            officer2_designation: proposal?.acquisition_incharge_designation || "",
            date: proposal?.acquisition_proposal_date
              ? proposal.acquisition_proposal_date
              : new Date().toISOString().split("T")[0],
            place: proposal?.acquisition_incharge_address || "",
          });
        }
      } catch (e) {
        console.warn("Proposal detail fetch failed (non-critical):", e.message);
      }
    } catch (err) {
      console.error("❌ LandholdingAllDocu fetch error:", err);
      setError(`डेटा लोड करताना त्रुटी: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [projectId]);


  // Get project and proposal IDs from localStorage (similar to Tehsildar.js)
  useEffect(() => {
    const getProjectInfo = () => {

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return project.project_id || project.id;
        } catch (e) {
          console.error('Error parsing selected project:', e);
        }
      }

      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return projectData.project_id || projectData.id;
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return user.data?.project_id || user.project_id;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      return null;
    };

    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;
        } catch (e) {
          console.error('Error parsing proposal data:', e);
        }
      }
      return null;
    };

    const projId = getProjectInfo();
    const propId = getProposalInfo();

    setProjectId(projId);
    setProposalId(propId);

    console.log('Project ID:', projId, 'Proposal ID:', propId);
  }, []);

const fetchParishisht4Document = async () => {
    if (!projectId || !proposalId) return;

    try {
      const response = await apiService.getDocuments(projectId, proposalId, 'AllDocuments');

      if (response.status === 200 && response.data) {
        const doc = Array.isArray(response.data) ? response.data[0] : response.data;
        if (doc?.upload_path || doc?.document) {
          setExistingParishisht4({
            document: doc.upload_path || doc.document,
            created_at: doc.created_at || new Date().toISOString(),
          });
        } else {
          setExistingParishisht4(null);
        }
      }
    } catch (err) {
      console.error("Parishisht-4 fetch error:", err);
      setExistingParishisht4(null);
    }
  };

  useEffect(() => {
    if (projectId && proposalId) {
      fetchParishisht4Document();
    }
  }, [projectId, proposalId]);

  {/* const handleUploadParishisht4 = async () => {
    if (!parishisht4File) {
      setErrorP4('कृपया फाइल निवडा');
      return;
    }

    try {
      setUploadingP4(true);
      setErrorP4(null);
      setSuccessP4(false);

      const formData = new FormData();
      formData.append('type', 'AllDocuments');
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('document', parishisht4File);

      const result = await apiService.postAddUpdateOtherDocuments(formData);

      const isSuccess =
        !result ||
        result.status === 200 ||
        result.status === 201 ||
        result.status === 'success' ||
        result.success === true ||
        result.data?.success === true ||
        result.data?.status === 200 ||
        result.data?.status === 201 ||
        result.message?.toLowerCase()?.includes('success') ||
        result.data?.message?.toLowerCase()?.includes('success') ||
        result.documents?.document;

      if (isSuccess) {
        setSuccessP4(true);
        setParishisht4File(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = ''; await fetchParishisht4Document();
        setTimeout(() => setSuccessP4(false), 4000);
      } else {
        setErrorP4('डॉक्युमेंट अपलोड करताना त्रुटी');
      }
    } catch (err) {
      console.error(err);
      setErrorP4('डॉक्युमेंट अपलोड करताना त्रुटी');
    } finally {
      setUploadingP4(false);
    }
  }; */}

  // ✅ Decide which document to send: signed PDF takes priority over uploaded file
  const getFinalDocument = () => {
    if (selectedSignDocument) return selectedSignDocument;
    if (parishisht4File) return parishisht4File;
    return null;
  };

  const handleUploadParishisht4 = async () => {
    const finalDocument = getFinalDocument();

    if (!finalDocument) {
      setErrorP4('कृपया फाइल निवडा');
      return;
    }

    try {
      setUploadingP4(true);
      setErrorP4(null);
      setSuccessP4(false);

      const formData = new FormData();
      formData.append('type', 'AllDocuments');
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('document', finalDocument);

      const result = await apiService.postAddUpdateOtherDocuments(formData);

      const isSuccess =
        !result ||
        result.status === 200 ||
        result.status === 201 ||
        result.status === 'success' ||
        result.success === true ||
        result.data?.success === true ||
        result.data?.status === 200 ||
        result.data?.status === 201 ||
        result.message?.toLowerCase()?.includes('success') ||
        result.data?.message?.toLowerCase()?.includes('success') ||
        result.documents?.document;

      if (isSuccess) {
        setSuccessP4(true);
        setParishisht4File(null);
        resetSignatureState();
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = ''; await fetchParishisht4Document();
        setTimeout(() => setSuccessP4(false), 4000);
      } else {
        setErrorP4('डॉक्युमेंट अपलोड करताना त्रुटी');
      }
    } catch (err) {
      console.error(err);
      setErrorP4('डॉक्युमेंट अपलोड करताना त्रुटी');
    } finally {
      setUploadingP4(false);
    }
  };

  const handleParishisht4FileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrorP4('कृपया फक्त PDF किंवा Image फाइल निवडा');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrorP4('फाइल साइज 10MB पेक्षा कमी असावी');
      return;
    }

    setParishisht4File(file);
    setErrorP4(null);
  };

  const handleViewP4 = (fileOrUrl) => {
    let url, isPdf;

    if (fileOrUrl instanceof File) {
      url = URL.createObjectURL(fileOrUrl);
      isPdf = fileOrUrl.type === 'application/pdf';
    } else {
      url = fileOrUrl;
      isPdf = url.toLowerCase().includes('.pdf');
    }

    setDocUrlP4(url);
    setIsPdfP4(isPdf);
    setShowDocViewerP4(true);
  };

  const closeViewerP4 = () => {
    setShowDocViewerP4(false);
    if (docUrlP4.startsWith('blob:')) URL.revokeObjectURL(docUrlP4);
    setDocUrlP4('');
    setIsPdfP4(false);
  };

  // ── Handle View Document ──
  const handleViewDocument = (fileOrUrl) => {
    if (!fileOrUrl) {
      console.warn("No file/URL provided");
      return;
    }

    if (fileOrUrl instanceof File) {
      const url = URL.createObjectURL(fileOrUrl);
      setDocUrl(url);
    } else {
      setDocUrl(fileOrUrl);
    }

    setShowDocModal(true);
  };

  // ── Print All ──
  const handlePrintAll = () => {
    if (!allDocRef.current) return;
    const content = allDocRef.current.innerHTML;
    const win = window.open("", "", "height=900,width=1200");
    win.document.write(`
<!DOCTYPE html>
<html lang="mr">
<head>
  <meta charset="UTF-8">
  <title>भूसंपादन – सर्व कागदपत्रे</title>
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 portrait;
      margin-top: 0.19in; margin-bottom: 0.25in;
      margin-left: 0.81in; margin-right: 0.5in;
    }
    body { font-family: 'Noto Sans Devanagari','Mangal',Arial,sans-serif; font-size: 11pt; }
    table { border-collapse: collapse; width: 100%; margin: 8px 0; }
    th, td { border: 1px solid black; padding: 4px; font-size: 9pt; text-align: center; }
    h2, h3 { text-align: center; margin: 4px 0; }
    .section-divider { page-break-before: always; }
    .doc-header-band { background: #1a3c5e !important; -webkit-print-color-adjust: exact; color: white !important; padding: 6px; font-weight: bold; margin-bottom: 12px; }
    .print\\:hide-in-print { display: none !important; }
    .certificate {
      max-width: none;
      background: white;
      page-break-inside: avoid;
      margin-bottom: 30px;
      padding: 20px;
    }

    .no-print{
    display: none;
    }
    
    /* Project and Proposal Header Styles */
    .flex {
      display: flex;
    }
    
    .justify-between {
      justify-content: space-between;
    }
    
    .items-center {
      align-items: center;
    }
    
    .font-semibold {
      font-weight: 600;
    }
    
    .mb-4 {
      margin-bottom: 16px;
    }
    
    .certificate-header {
      text-align: center;
      font-weight: bold;
      font-size: 18px;
      margin-bottom: 10px;
      text-decoration: underline;
    }

    .main-header {
      text-align: center;
      font-weight: bold;
      font-size: 18px;
      margin-bottom: 10px;
    }
    
    .certificate-content {
      text-align: justify;
      line-height: 2;
      margin: 20px 0;
    }
    
    .signature-section {
      display: flex;
      justify-content: space-between;
      margin-top: 40px;
      page-break-inside: avoid;
    }
    
    .signature-block {
      text-align: center;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
    }
    
    td, th {
      border: 1px solid black;
      padding: 8px;
      text-align: left;
    }
    
    .page-break {
      page-break-after: always;
    }
    
    @media print {
      body { -webkit-print-color-adjust: exact; }
      .print-hidden {
        display: none !important;
      }
  </style>
</head>
<body>
  ${content}
  <script>window.onload = function(){ window.focus(); window.print(); };</script>
</body>
</html>`);
    win.document.close();
  };

  // ─────── RENDER ───────
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
        <Loader2 size={36} className="animate-spin" style={{ color: "#1a3c5e" }} />
        <span style={{ marginLeft: 12, fontSize: 16, color: "#555" }}>डेटा लोड होत आहे...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        margin: "40px auto", maxWidth: 600, padding: 24,
        border: "1px solid #fca5a5", background: "#fff5f5", borderRadius: 8,
        display: "flex", alignItems: "flex-start", gap: 12,
      }}>
        <AlertCircle size={24} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>डेटा मिळवताना त्रुटी</p>
          <p style={{ color: "#555", fontSize: 13 }}>{error}</p>
          <button
            onClick={loadAll}
            style={{
              marginTop: 12, padding: "6px 16px",
              background: "#1a3c5e", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
            }}
          >
            पुन्हा प्रयत्न करा
          </button>
        </div>
      </div>
    );
  }

  const sharedProps = {
    data: landData,
    proposalData,
    proposalNo,
    formattedDate,
    inchargeName,
    inchargeDesig,
    regionalName,
    regionalDesig,
  };

  // Props specifically needed by Parishisht-6
  const p6Props = {
    ...sharedProps,
    projectName: proposalData?.project_name || "",
    locationInfo,
    inchargeAddress,
  };

  return (
    <div className="max-w-6xl mx-auto p-4 bg-gray-50 min-h-screen">

      {/* ── Top Action Bar ── */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "14px 20px",
        marginBottom: 24,
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <FileText size={24} color="#1a3c5e" />
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a3c5e" }}>
              सर्व कागदपत्रे
            </h1>
           
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handlePrintAll}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", background: "#2563eb", color: "#fff",
              border: "none", borderRadius: 7, cursor: "pointer", fontWeight: 600,
              fontSize: 13,
            }}
          >
            <Printer size={16} />
            Print All
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
      
              {/* Success / Error Messages */}
              {successP4 && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-800">
                  <CheckCircle className="w-5 h-5 mr-3" />
                  <span className="font-medium">डॉक्युमेंट यशस्वीरित्या अपलोड झाले!</span>
                </div>
              )}
      
              {errorP4 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-red-800">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-3" />
                    <span className="font-medium">{errorP4}</span>
                  </div>
                  <button onClick={() => setErrorP4(null)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
      
              {/* Upload Area */}
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    अपलोड करा
                  </label>
                  <input
                    id="parishisht4-file-input"
                    type="file"
                    accept="application/pdf,image/jpeg,image/jpg,image/png"
                    onChange={handleParishisht4FileChange}
                    className="block w-full text-sm text-gray-600
                       file:mr-4 file:py-2.5 file:px-5
                       file:rounded-lg file:border-0
                       file:text-sm file:font-semibold
                       file:bg-indigo-600 file:text-white
                       hover:file:bg-indigo-700
                       border border-gray-300 rounded-lg cursor-pointer"
                  />
                  {/* {parishisht4File && (
                    <p className="mt-2 text-sm text-green-600">
                      निवडलेली फाइल: {parishisht4File.name}
                    </p>
                  )} */}
                  
                {/* </div> */}
      
                {/* {parishisht4File && (
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleViewP4(parishisht4File)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
                    >
                      <Eye size={18} /> पूर्वावलोकन
                    </button>
      
                    <button
                      onClick={handleUploadParishisht4}
                      disabled={uploadingP4}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white transition-colors ${uploadingP4 ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                        }`}
                    >
                      {uploadingP4 ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" /> अपलोड होत आहे...
                        </>
                      ) : (
                        <>
                          <Upload size={18} /> सबमिट करा
                        </>
                      )}
                    </button>
                  </div>
                )} */}

                {parishisht4File && (
                    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-green-700 font-medium truncate max-w-[60%]">
                          निवडलेली फाइल: {parishisht4File.name}
                        </p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                          {(parishisht4File.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      {/* Sign Doc button — only for PDFs */}
                      {parishisht4File.type === 'application/pdf' && (
                        <div className="flex gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setPdfForSigning(parishisht4File);
                              setShowSignaturePad(true);
                            }}
                            disabled={uploadStatus === 'uploading'}
                            className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                          >
                            {uploadStatus === 'uploading' ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                Signing...
                              </>
                            ) : (
                              <>
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                Sign Doc
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleViewP4(
                              uploadStatus === 'success' ? selectedSignDocument : parishisht4File
                            )}
                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                          >
                            <Eye size={12} /> पूर्वावलोकन
                          </button>
                        </div>
                      )}
                      {/* Signature status messages */}
                      {uploadStatus === 'success' && (
                        <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                          ✅ Document Signed Successfully
                        </div>
                      )}
                      {uploadStatus === 'error' && uploadError && (
                        <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                          ❌ {uploadError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
      
                {parishisht4File && (
                  <div className="flex gap-4">
                    {/* Preview button for non-PDF files */}
                    {parishisht4File.type !== 'application/pdf' && (
                      <button
                        onClick={() => handleViewP4(parishisht4File)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
                      >
                        <Eye size={18} /> पूर्वावलोकन
                      </button>
                    )}
      
                    <button
                      onClick={handleUploadParishisht4}
                      disabled={uploadingP4}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white transition-colors ${uploadingP4 ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                        }`}
                    >
                      {uploadingP4 ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" /> अपलोड होत आहे...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          {uploadStatus === 'success' ? 'Signed Doc सबमिट करा' : 'सबमिट करा'}
                        </>
                      )}
                    </button>
                  </div>
                )}
      
                {/* Existing Document */}
                {existingParishisht4 && (
                  <div className="mt-8 p-5 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <FileText className="w-6 h-6 text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-800">अपलोड केलेला दस्तऐवज</p>
                          <p className="text-sm text-gray-600 mt-1">
                            अपलोड दिनांक: {new Date(existingParishisht4.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewP4(existingParishisht4.document)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Eye size={18} /> पाहा
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

      {/* Document Viewer Modal – reuse or duplicate the one you already have */}
      {showDocViewerP4 && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-6xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
            <div className="flex justify-between items-center p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-2xl font-bold text-gray-800">डॉक्युमेंट पूर्वावलोकन</h3>
              <button
                onClick={closeViewerP4}
                className="w-12 h-12 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 text-2xl font-bold flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <div className="h-[calc(95vh-80px)] bg-gray-50 flex items-center justify-center">
              {isPdfP4 ? (
                <iframe
                  src={docUrlP4}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={docUrlP4}
                  alt="Document"
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── All Documents (captured by ref for download/print) ── */}
      <div ref={allDocRef} id="all-documents-content">

        {/* ─── SECTION 0: ViewProposal (नमुना "क") ─── */}
        {viewProposalData && (
          <div className="section-divider">
            <PageWrapper
              title='नमुना "क" : भूसंपादन प्रस्ताव'
              proposalNo={viewProposalData.acquisition_proposal_no}
              projectName={proposalData?.project_name}
            >
              <ViewProposalContent
                proposal={viewProposalData}
                projectName={proposalData?.project_name}
                onViewDocument={handleViewDocument}
                formattedDate={todayFormatted}
                inchargeName={proposalData?.acquisition_incharge_name}
                inchargeDesig={proposalData?.acquisition_incharge_designation}
                regionalName={proposalData?.acquisition_regional_incharge_name}
                regionalDesig={proposalData?.acquisition_regional_incharge_designation}
              />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION 1: Parishisht-1 → Anusuchi-1 ─── */}
        <div className="section-divider">
          <PageWrapper
            title="परिशिष्ट - 1 | अनुसूची - 1 : जमिनींचा तपशील"
            proposalNo={proposalNo}
            projectName={proposalData?.project_name}
          >
            <Anusuchi1Content {...sharedProps} />
          </PageWrapper>
        </div>

        {/* ─── SECTION 2: Parishisht-1 → Anusuchi-2 Summary ─── */}
        <div className="section-divider">
          <PageWrapper
            title="परिशिष्ट - 1 | अनुसूची - 2 : जमिनीचा गोषवारा"
            proposalNo={proposalNo}
            projectName={proposalData?.project_name}
          >
            <Anusuchi2SummaryContent {...sharedProps} />
          </PageWrapper>
        </div>

        {/*Parishisht-2 : खाजगी जमिनीचा तपशील ─── */}
        {landData.length > 0 && (
          <div className="section-divider">
            <PageWrapper
              title="परिशिष्ट 2 : प्रस्तावामध्ये समाविष्ट खाजगी जमिनीचा तपशील"
              proposalNo={proposalNo}
              projectName={proposalData?.project_name}
            >
              <Parishisht2Content {...sharedProps} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION 3: Anusuchi-2 (घर क्रमांक detailed) ─── */}
        {gharData.length > 0 && (
        <div className="section-divider">
          <PageWrapper
            title="अनुसूची - 2 : घर क्रमांक तपशील"
            proposalNo={proposalNo}
            projectName={proposalData?.project_name}
          >
            <Anusuchi2DetailContent {...sharedProps} />
          </PageWrapper>
        </div>
        )}

        {/* ─── SECTION 4: Parishisht-3 / Anusuchi-3 ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="परिशिष्ट - 3 : भूमि सीमांकन प्रमाणपत्र"
              proposalNo={formData.proposal_no_name}
              projectName={formData.project_name}
            >
              <Anusuchi3Content formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION 5: Parishisht-6 : Chatuhsima Boundary Table ─── */}
        {landData.length > 0 && (
          <div className="section-divider">
            <PageWrapper
              title="परिशिष्ट - 6 : चतु:सिमा दर्शविणारा तक्ता"
              proposalNo={proposalNo}
              projectName={proposalData?.project_name}
            >
              <Parishisht6Content {...p6Props} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: पूर्वी भूसंपादन न झाल्याबाबत प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="पूर्वी भूसंपादन न झाल्याबाबत प्रमाणपत्र"
              proposalNo={formData.proposal_no_name}
              projectName={formData.project_name}
            >
              <NoPreviousAcquisitionCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: प्रस्ताव रद्द होणार नसल्याचे प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="प्रस्ताव रद्द होणार नसल्याचे प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <ProposalNotCancelledCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: संयुक्त मोजणी शुल्क शासन जमा करणे बाबत प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="संयुक्त मोजणी शुल्क शासन जमा करणे बाबत प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <SurveyFeeCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: निधी उपलब्धता प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="निधी उपलब्धता प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <FinancialProvisionCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: सार्वजनिक प्रयोजनाच्या स्वरुपाबाबत विवरण ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="सार्वजनिक प्रयोजनाच्या स्वरुपाबाबत विवरण"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <PublicPurposeDetailsCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: धार्मिक स्थळांबाबत प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="धार्मिक स्थळांबाबत प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <ReligiousPlacesCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: रेल्वे सीमा बाबत प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="रेल्वे सीमा बाबत प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <RailwayBoundaryCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

        {/* ─── SECTION: सार्वजनिक मार्गाधिकार प्रमाणपत्र ─── */}
        {formData && (
          <div className="section-divider">
            <PageWrapper
              title="सार्वजनिक मार्गाधिकार प्रमाणपत्र"
              proposalNo={formData.proposal_no_name || proposalNo}
              projectName={formData.project_name || proposalData?.project_name}
            >
              <PublicRightsOfWayCertificate formData={formData} />
            </PageWrapper>
          </div>
        )}

      </div>

      {/* ── Signature Pad Modal ── */}
            {showSignaturePad && pdfForSigning && (
              <SignaturePad
                pdfFile={pdfForSigning}
                onClose={() => {
                  setShowSignaturePad(false);
                  setPdfForSigning(null);
                }}
                onSign={(signedFile) => {
                  signDocument(pdfForSigning, signedFile);
                  setShowSignaturePad(false);
                  setPdfForSigning(null);
                }}
      
                onCancel={() => {
                  setShowSignaturePad(false);
                  setPdfForSigning(null);
              }}
              />
            )}

      {/* ── Document Viewer Modal ── */}
      {showDocModal && docUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "90%",
              maxWidth: 1200,
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#1f2937" }}>
                दस्तऐवज पूर्वावलोकन
              </h3>
              <button
                onClick={() => {
                  setShowDocModal(false);
                  if (docUrl?.startsWith("blob:")) {
                    URL.revokeObjectURL(docUrl);
                  }
                  setDocUrl("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#9ca3af",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 0,
                }}
                onMouseOver={(e) => (e.target.style.color = "#374151")}
                onMouseOut={(e) => (e.target.style.color = "#9ca3af")}
              >
                ×
              </button>
            </div>

            {/* Modal Body - iframe viewer */}
            <div style={{ flex: 1, padding: 16, overflow: "hidden" }}>
              <iframe
                src={docUrl}
                title="Document Viewer"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandholdingAllDocu;